import React, { RefObject, useContext, useEffect, useState } from 'react';
import cx from 'classnames';
import { useSelector } from '../../redux/modules/helpers/useSelector';
import { ImageButton, NavbarIcon } from './styles';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import styled, { keyframes } from 'styled-components/macro';
import { UserMenu } from './UserMenu';
import { HeaderContext, HeaderPopovers } from './HeaderProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from '@fortawesome/pro-light-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { ensure } from 'src/client/utils/type-utils';
import { TABLET_LANDSCAPE } from 'src/client/constants/mediaQueries';

type Props = {
  closeNotificationPanel: () => void;
  mainHeader: RefObject<HTMLDivElement>;
  toggleRecentsBox: () => void;
};

export const UserMenuButton = (props: Props) => {
  // User is checked outside of here before even loading this component.
  const user = ensure(useSelector((state) => state.auth.user));
  const siteNotificationBadgeCount = useSelector((state) => state.siteNotifications.items?.newNotificationsCount);
  const [bouncingBadge, setBounceingBadge] = useState(false);
  const { activePopover, setActivePopover } = useContext(HeaderContext);

  useEffect(() => {
    const toggleBouncingBadge = () => setBounceingBadge((bouncing) => !bouncing);
    let initialTimeout: NodeJS.Timeout | undefined = undefined;
    let recurringTimeout: NodeJS.Timeout | undefined = undefined;

    initialTimeout = setTimeout(() => {
      toggleBouncingBadge();
      recurringTimeout = setTimeout(toggleBouncingBadge, 20000);
    }, 2000);

    return () => {
      if (initialTimeout) {
        clearTimeout(initialTimeout);
      }

      if (recurringTimeout) {
        clearTimeout(recurringTimeout);
      }
    };
  }, []);

  const handleProfileMenuToggle = (next: boolean) => {
    if (next === false) {
      if (activePopover !== HeaderPopovers.PROFILE) return;
      return setActivePopover(null);
    }
    setActivePopover(HeaderPopovers.PROFILE);
  };

  const profileMenuOpen = activePopover === HeaderPopovers.PROFILE;

  return (
    <OverlayTrigger
      container={props.mainHeader}
      placement="bottom"
      trigger={[]}
      show={profileMenuOpen}
      overlay={
        <UserMenu
          toggleProfileMenu={handleProfileMenuToggle}
          onRecentsClick={props.toggleRecentsBox}
          closeNotificationPanel={props.closeNotificationPanel}
          username={user.email}
          siteNotificationBadgeCount={siteNotificationBadgeCount}
        />
      }
      rootClose
      onToggle={handleProfileMenuToggle}
    >
      <UserButton
        className={cx('clickable', { active: profileMenuOpen })}
        onClick={() => handleProfileMenuToggle(!profileMenuOpen)}
      >
        <NavbarIcon icon={faUserCircle} />
        <FontAwesomeIcon icon={faCaretDown} />
        {siteNotificationBadgeCount > 0 && (
          <NotificationBadge className={cx({ bouncing: bouncingBadge })}>
            {siteNotificationBadgeCount}
          </NotificationBadge>
        )}
      </UserButton>
    </OverlayTrigger>
  );
};

const UserButton = styled(ImageButton)`
  display: flex;
  position: relative;
  align-items: center;
  column-gap: 0.25rem;
  margin-left: auto;

  @media ${TABLET_LANDSCAPE} {
    margin-right: -5px;
  }
`;

const bounceAnimation = keyframes`
  0%,
  20%,
  40%,
  60%,
  80%,
  100% {
    transform: translateY(0);
  }
  
  50% {
    transform: translateY(-5px);
  }
`;

const NotificationBadge = styled.div`
  color: white;
  font-size: 0.8rem;
  left: 50%;
  top: 0;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background-color: ${(props) => props.theme.headerBarPrimary3};
  position: absolute;
  text-align: center;
  line-height: 22px;
  font-weight: 700;
  opacity: 1;

  &.bouncing {
    animation: ${bounceAnimation} 1.5s linear infinite;
  }
`;
